import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { ClickableUser } from "../contexts/UserSheetContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { toast } from "sonner";
import {
  Shield,
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
  User,
  FileText,
  Calendar,
  TrendingUp,
  Receipt,
  ClipboardList,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

// ── Config actions ──────────────────────────────────────────────────────────

const ACTION_CONFIG = {
  create_user:         { label: "Création utilisateur",          color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" },
  update_user:         { label: "Modification utilisateur",      color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" },
  deactivate_user:     { label: "Activation/désactivation",      color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400" },
  submit_ndf:          { label: "Soumission NDF",                color: "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400" },
  manager_approve_ndf: { label: "Approbation NDF (manager)",     color: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400" },
  approve_ndf:         { label: "Approbation NDF (finale)",      color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400" },
  reject_ndf:          { label: "Rejet NDF",                     color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400" },
  review_leave:        { label: "Révision congé",                color: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400" },
};

const RESOURCE_CONFIG = {
  user:  { label: "Utilisateur", icon: User },
  ndf:   { label: "NDF",         icon: Receipt },
  leave: { label: "Congé",       icon: Calendar },
};

const getInitials = (name) =>
  name.split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

const formatDate = (iso) => {
  try {
    return format(new Date(iso), "dd MMM yyyy HH:mm", { locale: fr });
  } catch {
    return iso;
  }
};

// ── AuditPage ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

const AuditPage = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [filterAction, setFilterAction] = useState("all");
  const [filterResource, setFilterResource] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchLogs = useCallback(async (p = 1) => {
    setIsLoading(true);
    try {
      const params = { page: p, page_size: PAGE_SIZE };
      if (search)         params.search        = search;
      if (filterAction !== "all")   params.action        = filterAction;
      if (filterResource !== "all") params.resource_type = filterResource;
      if (startDate)      params.start_date    = startDate;
      if (endDate)        params.end_date      = endDate;

      const res = await axios.get(`${API}/audit`, { params });
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setPage(p);
    } catch {
      toast.error("Erreur lors du chargement du journal d'audit");
    } finally {
      setIsLoading(false);
    }
  }, [search, filterAction, filterResource, startDate, endDate]);

  useEffect(() => {
    fetchLogs(1);
  }, [fetchLogs]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-['Playfair_Display'] text-xl md:text-3xl font-bold text-[#111827] dark:text-[#D6E4F5] flex items-center gap-3">
            <Shield className="w-7 h-7 text-[#1A4D2E] dark:text-[#4B8BF4]" />
            Journal d'audit
          </h1>
          <p className="text-[#4B5563] dark:text-[#7EA8CE] mt-1 text-sm">
            {total} action{total !== 1 ? "s" : ""} enregistrée{total !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="card-neumorphic">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            {/* Search */}
            <div className="relative sm:col-span-2 lg:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input
                placeholder="Rechercher par utilisateur, action…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
              />
            </div>

            {/* Action */}
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]">
                <SelectValue placeholder="Type d'action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les actions</SelectItem>
                {Object.entries(ACTION_CONFIG).map(([key, cfg]) => (
                  <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Resource type */}
            <Select value={filterResource} onValueChange={setFilterResource}>
              <SelectTrigger className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]">
                <SelectValue placeholder="Ressource" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les ressources</SelectItem>
                {Object.entries(RESOURCE_CONFIG).map(([key, cfg]) => (
                  <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Date de début */}
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs font-medium text-[#9CA3AF] dark:text-[#3D607E] shrink-0">Du</span>
              <Input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="flex-1 min-w-0 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
              />
            </div>

            {/* Date de fin */}
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs font-medium text-[#9CA3AF] dark:text-[#3D607E] shrink-0">Au</span>
              <Input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="flex-1 min-w-0 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
              />
            </div>
          </div>

          {/* Reset */}
          {(search || filterAction !== "all" || filterResource !== "all" || startDate || endDate) && (
            <div className="mt-3 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch(""); setFilterAction("all");
                  setFilterResource("all"); setStartDate(""); setEndDate("");
                }}
                className="text-[#4B5563] dark:text-[#7EA8CE] text-xs"
              >
                Réinitialiser les filtres
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table / Cards */}
      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E] dark:text-[#4B8BF4]" />
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16 text-[#9CA3AF] dark:text-[#3D607E]">
          <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Aucune action trouvée</p>
        </div>
      ) : (
        <>
          {/* ── Mobile : cartes ── */}
          <div className="sm:hidden space-y-3">
            {logs.map(log => {
              const ac = ACTION_CONFIG[log.action] || { label: log.action, color: "bg-gray-100 text-gray-700" };
              const rc = RESOURCE_CONFIG[log.resource_type] || { label: log.resource_type, icon: FileText };
              const ResourceIcon = rc.icon;
              return (
                <div key={log.id} className="border dark:border-[#1A3050] rounded-2xl bg-white dark:bg-[#0B1628] p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-9 h-9 shrink-0">
                      <AvatarImage src={log.user_avatar} className="object-cover" />
                      <AvatarFallback className="text-xs font-semibold bg-gradient-to-br from-[#1A4D2E] to-[#2D7A4E] text-white">
                        {getInitials(log.user_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-[#111827] dark:text-[#D6E4F5] truncate"><ClickableUser userId={log.user_id}>{log.user_name}</ClickableUser></p>
                      <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E]">{formatDate(log.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge className={`text-[10px] px-2 border-0 ${ac.color}`}>{ac.label}</Badge>
                    <Badge className="text-[10px] px-2 border-0 bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400 flex items-center gap-1">
                      <ResourceIcon className="w-3 h-3" />{rc.label}
                    </Badge>
                  </div>
                  {log.resource_label && (
                    <p className="text-xs text-[#4B5563] dark:text-[#7EA8CE] font-medium truncate">{log.resource_label}</p>
                  )}
                  {log.details && (
                    <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] whitespace-pre-wrap break-words">{log.details}</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Desktop : tableau ── */}
          <Card className="hidden sm:block card-neumorphic">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-[#1A3050]">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#6B7280] dark:text-[#7EA8CE] uppercase tracking-wide">Utilisateur</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#6B7280] dark:text-[#7EA8CE] uppercase tracking-wide">Action</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#6B7280] dark:text-[#7EA8CE] uppercase tracking-wide">Ressource</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#6B7280] dark:text-[#7EA8CE] uppercase tracking-wide">Détails</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#6B7280] dark:text-[#7EA8CE] uppercase tracking-wide">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-[#1A3050]">
                    {logs.map(log => {
                      const ac = ACTION_CONFIG[log.action] || { label: log.action, color: "bg-gray-100 text-gray-700" };
                      const rc = RESOURCE_CONFIG[log.resource_type] || { label: log.resource_type, icon: FileText };
                      const ResourceIcon = rc.icon;
                      return (
                        <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-[#112240] transition-colors">
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2.5">
                              <Avatar className="w-8 h-8 shrink-0">
                                <AvatarImage src={log.user_avatar} className="object-cover" />
                                <AvatarFallback className="text-xs font-semibold bg-gradient-to-br from-[#1A4D2E] to-[#2D7A4E] text-white">
                                  {getInitials(log.user_name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-[#111827] dark:text-[#D6E4F5] leading-tight"><ClickableUser userId={log.user_id}>{log.user_name}</ClickableUser></p>
                                <p className="text-[10px] text-[#9CA3AF] dark:text-[#3D607E] capitalize">{log.user_role}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3">
                            <Badge className={`text-[10px] px-2 py-0.5 border-0 ${ac.color}`}>{ac.label}</Badge>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-1.5 text-[#4B5563] dark:text-[#7EA8CE]">
                              <ResourceIcon className="w-3.5 h-3.5 shrink-0" />
                              <span className="text-xs">{log.resource_label || rc.label}</span>
                            </div>
                          </td>
                          <td className="px-5 py-3">
                            <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] whitespace-pre-wrap break-words">
                              {log.details || "—"}
                            </p>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap">
                            <p className="text-xs text-[#4B5563] dark:text-[#7EA8CE]">{formatDate(log.created_at)}</p>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#9CA3AF] dark:text-[#3D607E]">
                Page {page} / {totalPages} — {total} entrée{total !== 1 ? "s" : ""}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchLogs(page - 1)}
                  disabled={page <= 1}
                  className="dark:border-[#1A3050] dark:text-[#7EA8CE]"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchLogs(page + 1)}
                  disabled={page >= totalPages}
                  className="dark:border-[#1A3050] dark:text-[#7EA8CE]"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuditPage;
